import React from "react"
import { Box, Button, Dialog, IconButton, Skeleton, Tab, Tabs, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { IconSeeker } from "@ui-seeker/core"

import { dsColorNew } from "@palette"


export const BRAND_GRADIENT = `linear-gradient(135deg, ${dsColorNew.webBlue500}, ${dsColorNew.cyan})`

// The app's dialog: white, rounded, with AppDialogHeader / AppDialogBody / AppDialogFooter inside
export default function AppDialog({ open, onClose, maxWidth = 'md', paperSx = {}, children, ...props }) {
	return <Dialog
		open={open}
		onClose={onClose}
		maxWidth={maxWidth}
		fullWidth
		{...props}
		PaperProps={{
			...props?.PaperProps,
			sx: {
				borderRadius: props?.fullScreen ? 0 : '16px', overflow: 'hidden', boxShadow: `0 24px 64px ${alpha(dsColorNew.navy, 0.3)}`,
				...paperSx, ...props?.PaperProps?.sx,
			},
		}}
	>
		{children}
	</Dialog>
}

// Icon tile, title, a line about what it shows, optional actions and the ×. `loading` keeps its place while the data arrives.
// `tone="navy"` paints it on the navy of the table headers and the support chat, with white text (the News popup).
export function AppDialogHeader({ icon, title, subtitle, actions, onClose, loading = false, tone = 'default', sx = {} }) {
	const navy = tone === 'navy'

	return <Box sx={{
		display: 'flex', alignItems: subtitle || loading ? 'flex-start' : 'center', gap: 1.75, px: 3, pt: 2.5, pb: 2,
		...(navy && { backgroundColor: dsColorNew.tableHead, pb: 2.5 }),
		...sx,
	}}>
		{!!icon && <Box sx={{
			width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
			display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
			// on navy the brand gradient fights the background: a quiet white tile instead
			background: navy ? alpha(dsColorNew.white, 0.12) : BRAND_GRADIENT, color: dsColorNew.white,
		}}>
			<IconSeeker icon={icon} sx={{ fontSize: 17 }}/>
		</Box>}
		<Box sx={{ minWidth: 0 }}>
			<Typography component="h2" sx={{ fontSize: 17, fontWeight: 600, color: navy ? dsColorNew.white : dsColorNew.textTitle, lineHeight: 1.3 }}>
				{loading ? <Skeleton width={220}/> : title}
			</Typography>
			{(!!subtitle || loading) && <Typography component="div" sx={{ fontSize: 13, color: navy ? alpha(dsColorNew.white, 0.75) : dsColorNew.textMuted, mt: 0.4 }}>
				{loading ? <Skeleton width={160}/> : subtitle}
			</Typography>}
		</Box>
		{!!actions && <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>{actions}</Box>}
		<IconButton aria-label="Close" onClick={onClose} sx={{
			ml: actions ? 0.5 : 'auto', width: 32, height: 32, borderRadius: '8px', color: navy ? alpha(dsColorNew.white, 0.75) : dsColorNew.textSubtle,
			...(navy && { '&:hover': { color: dsColorNew.white, backgroundColor: dsColorNew.navyRaisedHover } }),
		}}>
			<IconSeeker icon="xmark" sx={{ fontSize: 15 }}/>
		</IconButton>
	</Box>
}

// Thin tabs under the header: [{ value, label, icon?, disabled?, end? }]
export function AppDialogTabs({ value, onChange, tabs = [] }) {
	return <Tabs
		value={value}
		onChange={onChange}
		sx={{
			px: 3, minHeight: 0,
			borderBottom: `1px solid ${dsColorNew.grayBorder}`,
			'& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', backgroundColor: dsColorNew.appBlue },
			'& .MuiTabs-flexContainer': { gap: 3 },
		}}
	>
		{tabs.map(({ value: tabValue, label, icon, end, disabled }) => <Tab
			key={tabValue}
			value={tabValue}
			disabled={disabled}
			disableRipple
			label={<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
				{!!icon && <IconSeeker icon={icon} sx={{ fontSize: 14 }}/>}
				{label}
				{end}
			</Box>}
			sx={{
				minWidth: 0, minHeight: 0, px: 0.25, py: 1.5,
				textTransform: 'none', fontSize: 14, fontWeight: 500,
				color: dsColorNew.textMuted,
				'&.Mui-selected': { color: dsColorNew.textTitle },
				'&.Mui-disabled': { color: dsColorNew.textSubtle },
			}}
		/>)}
	</Tabs>
}

export function AppDialogBody({ children, sx = {} }) {
	return <Box sx={{ px: 3, pt: 1, pb: 2.5, ...sx }}>{children}</Box>
}

export function AppDialogFooter({ children }) {
	return <Box sx={{
		display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1,
		px: 3, py: 1.75, borderTop: `1px solid ${dsColorNew.grayBorder}`, backgroundColor: dsColorNew.tableRowAlt,
	}}>
		{children}
	</Box>
}

// The quiet "Cancel" of a dialog footer
export function AppDialogCancel({ children = 'Cancel', sx = {}, ...props }) {
	return <Button variant="text" {...props} sx={{
		height: 36, px: 1.75, borderRadius: '8px',
		color: dsColorNew.textMuted, backgroundColor: 'transparent', boxShadow: 'none',
		textTransform: 'none', fontSize: 14, fontWeight: 500,
		'&:hover': { backgroundColor: dsColorNew.pillTrackBg, boxShadow: 'none' },
		...sx,
	}}>
		{children}
	</Button>
}
